import { Capacitor } from '@capacitor/core';
import { Button, Stack, Typography } from '@mui/material';
import { useEffectOnce } from 'react-use';

import { DynamicIcon } from '@frontend-libs/atomic';

import { View, ViewSection } from '@frontend/components';
import { tApp, useI18n } from '@frontend/locales';

import { useMetadata } from '../metadata-context';
import { useSplashScreenHider } from '../use-splash-screen-hider';
import { VersionExpectationStateType } from './version-expectations.context';

export interface VersionUpdateViewProps {
  state: VersionExpectationStateType;
  onIgnoreUpdate(): void;
}

export const VersionUpdateView = (props: VersionUpdateViewProps) => {
  // We rely on i18n to render text and by using the hook here
  // we get "notified" on its state changes allowing us to show text.
  // This is needed since this view is displayed very early in the
  // bootstrapping and will so quickly that i18n has not finished
  // initializing.
  useI18n();

  const { hideSplashScreen } = useSplashScreenHider();
  const { appId, iosAppStoreId } = useMetadata();
  const isIos = Capacitor.getPlatform() === 'ios';

  const header =
    props.state === 'MINIMUM_OUTDATED'
      ? tApp.versionUpdate.optionalHeader()
      : tApp.versionUpdate.requiredHeader();

  const description =
    props.state === 'MINIMUM_OUTDATED'
      ? tApp.versionUpdate.optionalDescription()
      : tApp.versionUpdate.requiredDescription();

  const storeUrl = isIos
    ? iosAppStoreId
      ? `itms-apps://itunes.apple.com/app/id${iosAppStoreId}`
      : undefined
    : `https://play.google.com/store/apps/details?id=${appId}`;

  useEffectOnce(() => {
    hideSplashScreen();
  });

  return (
    <View>
      <Stack
        spacing={6}
        height="100vh"
        alignItems="center"
        justifyContent="center"
      >
        <ViewSection>
          <Stack spacing={2}>
            <DynamicIcon iconId="update" height={128} />

            <Typography variant="h3" component="h1" textAlign="center">
              {header}
            </Typography>

            <Typography
              variant="body1"
              color="textSecondary"
              textAlign="center"
            >
              {description}
            </Typography>
          </Stack>
        </ViewSection>

        <ViewSection>
          <Stack spacing={1}>
            {storeUrl ? (
              <Button variant="contained" href={storeUrl}>
                {tApp.versionUpdate.updateButton()}
              </Button>
            ) : (
              <Typography
                variant="body1"
                color="textSecondary"
                textAlign="center"
                pb={2}
              >
                {tApp.versionUpdate.iosAppStoreIdMissing()}
              </Typography>
            )}

            {props.state === 'MINIMUM_OUTDATED' && (
              <Button variant="outlined" onClick={props.onIgnoreUpdate}>
                {tApp.versionUpdate.skipButton()}
              </Button>
            )}
          </Stack>
        </ViewSection>
      </Stack>
    </View>
  );
};
